import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';

import * as HttpStatus from 'http-status-codes';
import { murmur3 } from 'murmurhash-js';
import { Observable, Subject, Subscription, of, share } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';

import { CurrentUserStateService } from '../../Core/Users/services/current-user-state.service';
import { CoreDialogService } from '../../Core/Dialogs/services/dialog.service';
import { MfaStatusModel, MfaStatusTypes } from '../../Core/Users/models/mfa.model';
import { UserStateModel } from '../../Core/Users/models/user-state.model';
import { AuthenticationError } from '../services/error-handler.service';
import { SessionManagementService, SessionStateEnum } from '../services/session-management.service';

/** Uaws by SessionManagementService to force the isInLimbo state without relying on an actual api call */
class NoOpHandler extends HttpHandler {
    handle(request: HttpRequest<any>): Observable<HttpEvent<any>> {
        return of(null);
    }
}

@Injectable()
export class SessionManagementInterceptor implements HttpInterceptor {
    private readonly sessionExpiredKey = 'SessionExpiredDialog';

    cachedRequests: Map<string, Observable<HttpEvent<any>>> = new Map();
    currentUserState: UserStateModel = null;
    currentUserStateService: CurrentUserStateService;
    dialogService: CoreDialogService;
    loginDialog: Observable<any> = null;

    isInLimbo: boolean = false;

    private loginSuccessful$ = new Subject<void>();
    /** Emits when user fails login, cancels (completes) all "paused" observables */
    private loginUnsuccessful$ = new Subject<void>();

    private subscriptions: Subscription[] = [];

    constructor(
        // We can't inject the currentUserState or coreDialog directly as that results in a circular dependency, inject it later
        private injector: Injector,
        private sessionManagementService: SessionManagementService
    ) {
        this.subscriptions.push(
            this.sessionManagementService.expiredSessionRefreshed$.subscribe(() => {
                this.isInLimbo = false;
                if (!this.dialogService) {
                    this.dialogService = this.injector.get(CoreDialogService);
                }
                this.dialogService.close(this.sessionExpiredKey);
                this.unsetLimboFromSuccess();
            }),
            this.sessionManagementService.userSessionExpired$.pipe(
                // Used in place of a real call to users/me, as its difficult to time and control otherwise
                switchMap(() => {
                    if (this.isInLimbo) {
                        return of(null);
                    }
                    const forcedError = new HttpErrorResponse({
                        status: HttpStatus.UNAUTHORIZED,
                        statusText: 'Service Unavailable',
                        url: 'noop'
                    });
                    const req = new HttpRequest('POST', 'noop', {});
                    const handler = new NoOpHandler();
                    return this.errorHandler(forcedError, req, handler);
                })
            ).subscribe()
        );
    }

    /**
    * Intercepts API calls, acts before they are called,
    * and sends them along if the response is allowed
    *
    * to reduce duplicate calls, we created a caching system that would map already triggered requests
    * the key is the stringified request hashed,
    * and the value is the requestStream
    * we then remove it from the map, when the API call is complete
    * so if the key exists, to just returned the mapped request instead of making another API call
    */
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.currentUserStateService) {
            this.currentUserStateService = this.injector.get(CurrentUserStateService);
            this.currentUserStateService.getState().subscribe(
                (state) => {
                    this.currentUserState = state;
                }
            );
        }
        if (!this.dialogService) {
            this.dialogService = this.injector.get(CoreDialogService);
        }

        const requestKeyHash = murmur3(JSON.stringify(request));

        // Since stringifying FormData results in an empty object, we cannot accurately hash it
        // For now we will ignore caching these types of requests
        if (!(request.body instanceof FormData) && this.cachedRequests.has(requestKeyHash)) {
            return this.cachedRequests.get(requestKeyHash);
        }

        if (this.isInLimbo) {
            // Pause all new requests until re-login workflow completes
            const requestStream: Observable<HttpEvent<any>> = this.loginSuccessful$
                .pipe(
                    switchMap(() => next.handle(request)),
                    catchError((error: HttpErrorResponse) => {
                        return this.errorHandler(error, request, next);
                    }),
                    tap({
                        next: event => this.successHandler(event),
                        complete: () => this.cachedRequests.delete(requestKeyHash)
                    }),
                    takeUntil(this.loginUnsuccessful$),
                    share()
                );

            this.cachedRequests.set(requestKeyHash, requestStream);
            return requestStream;
        } else {
            const requestStream: Observable<HttpEvent<any>> = next.handle(request)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return this.errorHandler(error, request, next);
                    }),
                    tap({
                        next: event => this.successHandler(event),
                        complete: () => this.cachedRequests.delete(requestKeyHash)
                    }),
                    share()
                );

            this.cachedRequests.set(requestKeyHash, requestStream);
            return requestStream;
        }
    }

    /**
    * Handles HTTP success responses from API calls
    */
    private successHandler(event: any): void {
        const hasEvent: boolean = event !== null && typeof event === 'object';
        let shouldIgnoreSuccess: boolean = false;

        if (hasEvent) {
            shouldIgnoreSuccess = this.shouldIgnoreApiSuccess(event.url);
        }
    }

    /**
    * Handles HTTP error responses from API calls
    */
    private errorHandler(error: HttpErrorResponse, request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        // when you call the "login/credentials" to change password, if you pass in the wrong currently password it will return you a 401
        const isUnauthorized: boolean = error.status === HttpStatus.UNAUTHORIZED && !request?.url?.includes('login/credentials');
        const mfaError: boolean = Object.values(MfaStatusTypes).includes(error.error?.status);

        if (mfaError) {
            throw new MfaStatusModel(error.error);
        }

        if (isUnauthorized) {
            if (this.currentUserState) {
                // User was authenticated, but session expired, let them re-authenticate
                if (!this.isInLimbo) {
                    this.isInLimbo = true;
                    this.loginDialog = this.dialogService.openWithObservable(
                        'SessionExpiredDialogComponent',
                        {
                            inputs: {
                                currentUserState: this.currentUserState
                            },
                            outputs: {},
                        },
                        null,
                        this.sessionExpiredKey
                    );
                }
                return this.loginDialog.pipe(
                    switchMap((close) => {
                        this.isInLimbo = false;
                        if (!close) {
                            this.sessionManagementService.setSessionState(SessionStateEnum.UNAUTHENTICATED);
                            this.loginUnsuccessful$.next();
                            throw new AuthenticationError();
                        }
                        this.unsetLimboFromSuccess();
                        return next.handle(request);
                    }),
                );
            } else {
                // User never authenticated
                this.sessionManagementService.setSessionState(SessionStateEnum.UNAUTHENTICATED);
                throw new AuthenticationError();
            }
        }

        throw error;
    }

    private unsetLimboFromSuccess() {
        // Login successful - Retry original request
        this.loginSuccessful$.next();
    }

    /**
    * Determines whether or not the provided URL
    * should be ignored for the purposes of continuing
    * the user's session
    */
    private shouldIgnoreApiSuccess(url: string): boolean {
        const currentOrigin: string = window.location.origin;
        const isConfigApiCall: boolean = url === `${currentOrigin}/api/neptune/configuration`;
        const hasUrl: boolean = typeof url === 'string';
        let shouldIgnoreApiSuccess: boolean = false;

        shouldIgnoreApiSuccess = !hasUrl || isConfigApiCall;

        return shouldIgnoreApiSuccess;
    }
}
